import React from 'react';
import {FlexColumn, FlexRow, IconTooltip, SubHeading, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {defaultTaxRateDisplay, formatTaxPercent, XeroDefaultTaxRate} from './ExportDefaultTaxRate.display';

type Props = {
	// The caller's `isXeroInvoice && isXeroInvoicingImprovementsEnabledOrOff()`; false renders nothing at all.
	show: boolean;
	rate: XeroDefaultTaxRate | null | undefined;
	organisation: string | null | undefined;
};

/*
 * Read-only: the client's Xero default tax rate, in the export header beside Bill From / Bill To.
 * Plain text, so "can't be edited" holds by construction. With no organisation selected the rate
 * cannot be judged: show a dash and no tooltip (D6).
 */
const ExportDefaultTaxRateField = ({show, rate, organisation}: Props) => {
	const {formatMessage, formatNumber} = useIntl();
	if (!show) {
		return null;
	}
	const display = defaultTaxRateDisplay(rate, organisation, pct => formatTaxPercent(formatNumber, pct));
	const value = display.kind === 'SET' ? display.label : display.kind === 'NONE' ? formatMessage({id: 'common.none'}) : '–';

	return (
		<FlexColumn>
			<FlexRow alignItems={'center'}>
				<SubHeading>
					<FormattedMessage id={'invoicing.export.default_tax_rate'} />
				</SubHeading>
				{display.kind !== 'UNKNOWN' && (
					<IconTooltip>
						<IconTooltip.Item
							title={formatMessage({id: 'invoicing.export.default_tax_rate'})}
							details={formatMessage({
								id:
									display.kind === 'SET'
										? 'invoicing.export.default_tax_rate_tooltip_set'
										: 'invoicing.export.default_tax_rate_tooltip_none',
							})}
						/>
					</IconTooltip>
				)}
			</FlexRow>
			<Text ellipsis tooltipOnEllipsis>
				{value}
			</Text>
		</FlexColumn>
	);
};

export default ExportDefaultTaxRateField;
